import { SVG, Svg, G, Point, Container, Box, Element as SvgElement
       } from '@svgdotjs/svg.js';
import { StageConstants } from 'core/constants';

import { PositioningHints
       } from '../../collage-designer/stage/placeholder/overlays/action-bar/action-bar.component'; // eslint-disable-line
import { HandleBase, Placement, Position, RoughBox } from './handle.base';
import { MoveHandle } from './handle.move';
import { RotationHandle } from './handle.rotation';
import { ScaleHandle, ScaleHandlePosition } from './handle.scale';

export interface TransformationConstraints {
  minWidth: number;
  minHeight: number;
}

export class TransformHandle {
  private handles: Array<HandleBase>;
  private group: G;
  private handleGroup: G;
  private doc: Svg;
  private constraints: TransformationConstraints;

  constructor(
    private element: SvgElement,
    constraints?: TransformationConstraints,
    private onUpdateHandler?: () => {},
    private onDragEndHandler?: () => {},
    private onClickHandler?: () => {})
  {
    this.doc = (<any>element).root() as Svg;
    this.group = ensureRotationGroup(element);
    this.handleGroup = (<any>element).parent().group()
      .addClass(StageConstants.GROUP_TAG_TRANSFORM_HANDLE);

    (<any>element).parent().front();
    this.handleGroup.front();

    this.handles = [
      new MoveHandle(element, this.handleGroup),
      new ScaleHandle(element, ScaleHandlePosition.TopLeft, this.handleGroup),
      new ScaleHandle(element, ScaleHandlePosition.TopCenter, this.handleGroup),
      new ScaleHandle(element, ScaleHandlePosition.TopRight, this.handleGroup),
      new ScaleHandle(
          element, ScaleHandlePosition.MiddleRight, this.handleGroup),
      new ScaleHandle(
          element, ScaleHandlePosition.BottomLeft, this.handleGroup),
      new ScaleHandle(
          element, ScaleHandlePosition.BottomCenter, this.handleGroup),
      new ScaleHandle(
          element, ScaleHandlePosition.BottomRight, this.handleGroup),
      new ScaleHandle(
          element, ScaleHandlePosition.MiddleLeft, this.handleGroup),
      new RotationHandle(element, this.handleGroup),
    ];

    for (const handle of this.handles) {
      handle.onRequestUpdate = this.update.bind(this);
      handle.onDragEnd = this.dragEnd.bind(this);

      if (this.onClickHandler) {
        handle.onClick = this.onClickHandler;
      }
    }

    this.constraints = constraints || {
      minWidth: this.element.width() as number,
      minHeight: this.element.height() as number,
    };

    this.setPlace({});
  }

  public teardown(): void
  {
    for (const handle of this.handles) {
      handle.teardown();
    }
    this.handleGroup.remove();
  }

  public get actionBarPositioningHints(): PositioningHints
  {
    return this.handles.reduce(
      (hints, handle) => ({...hints, ...handle.actionBarPositioningHints}),
      {});
  }

  private update(place: Placement): void
  {
    this.setPlace(place);
    if (this.onUpdateHandler) {
      this.onUpdateHandler();
    }
  }

  private setPlace(place: Placement): void
  {
    const limitWidth =
      (place.width && place.width < this.constraints.minWidth);

    const limitHeight =
      (place.height && place.height < this.constraints.minHeight);

    const newPlace = new Box(
      (! limitWidth && place.x) || this.element.x() as number,
      (! limitHeight && place.y) || this.element.y() as number,
      (! limitWidth && place.width) || this.element.width() as number,
      (! limitHeight && place.height) || this.element.height() as number
    );

    const elementToDoc = (
      this.doc.screenCTM().inverse().multiply(this.group.screenCTM()));

    const newCenter = (new Point(
      newPlace.x + (newPlace.w / 2),
      newPlace.y + (newPlace.h / 2)
    ).transform(elementToDoc) as any) as Position;

    const bounds = this.doc.viewbox();

    if (newCenter.x < bounds.x ||
        newCenter.y < bounds.y ||
        newCenter.x > bounds.x + bounds.width ||
        newCenter.y > bounds.y + bounds.height)
    {
      return;
    }

    this.element.x(newPlace.x);
    this.element.y(newPlace.y);
    this.element.width(newPlace.width);
    this.element.height(newPlace.height);

    if (place.rotation !== undefined) {
      this.group.rotate(
          place.rotation - <any>this.group.transform('rotate'),
          this.element.cx(),
          this.element.cy());
    }

    const elementBounds: RoughBox = {
      x: this.element.x() as number,
      y: this.element.y() as number,
      width: this.element.width() as number,
      height: this.element.height() as number,
    };

    for (const handle of this.handles) {
      handle.update(elementBounds);
    }
  }

  private dragEnd(): void
  {
    if (this.onDragEndHandler) {
      this.onDragEndHandler();
    }
    for (const handle of this.handles) {
      handle.dragEnd();
    }
  }
}

function ensureRotationGroup(element: SvgElement): G {
  const parent = (<any>element).parent() as Container;

  if (parent.node.tagName !== 'g') {
    const wrapper = parent.group();
    element.putIn(wrapper);
    return wrapper;
  } else {
    return parent as G;
  }
}
